import { useMemo } from 'react';
import { cn } from '@/lib/cn';

/* ==========================================================================
   Sparkline — lightweight SVG mini-chart for measurement trends.

   No external chart library. Pure SVG with smooth bezier curves,
   optional delta label, subtle grid, responsive width.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

export interface SparklineDataPoint {
  date: string;
  value: number;
}

export interface SparklineProps {
  data: SparklineDataPoint[];
  label: string;
  unit: string;
  width?: number;
  height?: number;
  color?: string;
  showDelta?: boolean;
  className?: string;
}

/* --------------------------------------------------------------------------
   Helpers
   -------------------------------------------------------------------------- */

/**
 * Generate a smooth bezier path through a set of points.
 * Uses Catmull-Rom to cubic bezier conversion for natural curves.
 */
function smoothPath(points: Array<{ x: number; y: number }>): string {
  if (points.length < 2) return '';
  if (points.length === 2) {
    return `M ${points[0].x},${points[0].y} L ${points[1].x},${points[1].y}`;
  }

  let d = `M ${points[0].x},${points[0].y}`;

  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(points.length - 1, i + 2)];

    // Catmull-Rom to cubic bezier control points
    const tension = 0.3;
    const cp1x = p1.x + (p2.x - p0.x) * tension;
    const cp1y = p1.y + (p2.y - p0.y) * tension;
    const cp2x = p2.x - (p3.x - p1.x) * tension;
    const cp2y = p2.y - (p3.y - p1.y) * tension;

    d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
  }

  return d;
}

/**
 * Generate a closed area path below the sparkline for the gradient fill.
 */
function areaPath(
  points: Array<{ x: number; y: number }>,
  height: number,
): string {
  if (points.length < 2) return '';
  const line = smoothPath(points);
  const lastPoint = points[points.length - 1];
  const firstPoint = points[0];
  return `${line} L ${lastPoint.x},${height} L ${firstPoint.x},${height} Z`;
}

function formatDelta(first: number, last: number, unit: string): string {
  const diff = last - first;
  const sign = diff >= 0 ? '+' : '';
  const formatted = Number.isInteger(diff) ? diff.toString() : diff.toFixed(1);
  return `${sign}${formatted}${unit}`;
}

function formatShortDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return new Intl.DateTimeFormat('it-IT', {
      day: 'numeric',
      month: 'short',
    }).format(d);
  } catch {
    return '';
  }
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function Sparkline({
  data,
  label,
  unit,
  width = 280,
  height = 80,
  color,
  showDelta = true,
  className,
}: SparklineProps) {
  // Use CSS variable for default color — terracotta accent
  const strokeColor = color ?? 'var(--color-accent-primary)';

  const normalizedPoints = useMemo(() => {
    if (data.length === 0) return [];

    const values = data.map((d) => d.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1; // avoid division by zero

    const paddingX = 8;
    const paddingY = 12;
    const chartWidth = width - paddingX * 2;
    const chartHeight = height - paddingY * 2;

    return data.map((d, i) => ({
      x: paddingX + (i / Math.max(1, data.length - 1)) * chartWidth,
      y: paddingY + (1 - (d.value - min) / range) * chartHeight,
    }));
  }, [data, width, height]);

  if (data.length < 2) {
    return (
      <div className={cn('flex flex-col gap-1', className)}>
        <span className="text-xs font-[family-name:var(--font-heading)] font-semibold text-text-secondary">
          {label}
        </span>
        <span className="text-xs text-text-muted">
          Dati insufficienti per il grafico
        </span>
      </div>
    );
  }

  const firstValue = data[0].value;
  const lastValue = data[data.length - 1].value;
  const delta = lastValue - firstValue;
  const deltaPositive = delta >= 0;

  // Unique ID for gradient
  const gradientId = `sparkline-grad-${label.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {/* Label row */}
      <div className="flex items-center justify-between">
        <span className="text-xs font-[family-name:var(--font-heading)] font-semibold text-text-secondary">
          {label}
        </span>
        {showDelta && (
          <span
            className={cn(
              'text-xs font-[family-name:var(--font-heading)] font-bold',
              deltaPositive ? 'text-status-positive' : 'text-status-negative',
            )}
          >
            {formatDelta(firstValue, lastValue, unit)}
          </span>
        )}
      </div>

      {/* SVG chart */}
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        preserveAspectRatio="none"
        className="overflow-visible"
        role="img"
        aria-label={`Trend ${label}: da ${firstValue}${unit} a ${lastValue}${unit}`}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={strokeColor} stopOpacity={0.15} />
            <stop offset="100%" stopColor={strokeColor} stopOpacity={0.02} />
          </linearGradient>
        </defs>

        {/* Subtle horizontal grid lines */}
        {[0.25, 0.5, 0.75].map((pct) => (
          <line
            key={pct}
            x1={4}
            y1={12 + pct * (height - 24)}
            x2={width - 4}
            y2={12 + pct * (height - 24)}
            stroke="var(--color-border-default)"
            strokeWidth={0.5}
            strokeDasharray="3,4"
            opacity={0.5}
          />
        ))}

        {/* Area fill */}
        <path
          d={areaPath(normalizedPoints, height)}
          fill={`url(#${gradientId})`}
        />

        {/* Main line */}
        <path
          d={smoothPath(normalizedPoints)}
          fill="none"
          stroke={strokeColor}
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Data point dots */}
        {normalizedPoints.map((pt, i) => (
          <circle
            key={i}
            cx={pt.x}
            cy={pt.y}
            r={i === normalizedPoints.length - 1 ? 3.5 : 2}
            fill={
              i === normalizedPoints.length - 1
                ? strokeColor
                : 'var(--color-surface-card)'
            }
            stroke={strokeColor}
            strokeWidth={i === normalizedPoints.length - 1 ? 0 : 1.5}
          />
        ))}
      </svg>

      {/* Date labels below chart */}
      <div className="flex justify-between px-1">
        <span className="text-[10px] text-text-muted">
          {formatShortDate(data[0].date)}
        </span>
        <span className="text-[10px] text-text-muted">
          {formatShortDate(data[data.length - 1].date)}
        </span>
      </div>
    </div>
  );
}
